import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCallback, useEffect, useState } from "react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { tokenStore } from "../../lib/tokenStore.js";
import { aiProvidersApi } from "./api.js";
import { labelFor } from "./providers-panel/format.js";
import { BudgetsCard } from "./providers-panel/BudgetsCard.js";
import { ProviderCard } from "./providers-panel/ProviderCard.js";
import { ProviderRolesCard } from "./providers-panel/ProviderRolesCard.js";
import { SpendSection } from "./providers-panel/SpendSection.js";
import { WorkloadRoutingCard } from "./providers-panel/WorkloadRoutingCard.js";
import type {
  Budgets,
  ProviderInfo,
  ProviderStatus,
  SetBudgetsRequest,
  SetWorkloadProviderRequest,
  UsageSummary,
  WorkloadGroupRow,
  WorkloadRoutingStatus,
} from "./types.js";

/**
 * System Admin ▸ AI Providers (`/admin/ai-providers`).
 *
 * Shows which LLM provider the AI gateway uses for non-streaming
 * completions, which providers are available, and each one's live key
 * health + model. A system admin can switch the active provider at
 * runtime; the choice is persisted in the gateway DB and survives a
 * restart.
 *
 * Switching only affects the gateway's non-streaming completions (the
 * autonomous sales agent + server-to-server calls). Streaming chat
 * (Otto + support) stays Anthropic-only by design, and any per-call
 * `provider` override still wins — the panel states this.
 *
 * This file is the data orchestrator: it loads the provider status + the
 * (independent) spend usage and owns the switch confirmation. The provider
 * cards live in `providers-panel/ProviderCard`, the per-task role map in
 * `ProviderRolesCard`, the spend block in `SpendSection`, and the pure
 * label/USD helpers in `providers-panel/format`.
 *
 * Renders WITHOUT its own `<PageHeader>` — the shared "System Admin"
 * header + tab strip live in `AdminLayout.tsx`.
 */
export function ProvidersPanel(): JSX.Element {
  const accessToken = tokenStore.getAccess();
  const authHeader = accessToken ? `Bearer ${accessToken}` : undefined;

  const [status, setStatus] = useState<ProviderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingSwitch, setPendingSwitch] = useState<ProviderInfo | null>(null);
  // Spend is independent of provider status: it loads (and fails) on its
  // own so a meter hiccup never blocks the provider switch.
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  // Per-feature routing loads independently of the global switch: an
  // older gateway without the endpoint must degrade to "no table", not
  // blank the whole page.
  const [routing, setRouting] = useState<WorkloadRoutingStatus | null>(null);
  const [routingError, setRoutingError] = useState<string | null>(null);
  const [routingBusy, setRoutingBusy] = useState<string | null>(null);
  const [modelBusy, setModelBusy] = useState<string | null>(null);
  const [pendingFollow, setPendingFollow] = useState<WorkloadGroupRow | null>(null);
  // Budgets load on their own, like spend: an older gateway without the
  // endpoint shows an error on this card only.
  const [budgets, setBudgets] = useState<Budgets | null>(null);
  const [budgetsError, setBudgetsError] = useState<string | null>(null);
  const [budgetsBusy, setBudgetsBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    setStatus(null);
    setUsageError(null);
    setUsage(null);
    try {
      setStatus(await aiProvidersApi.status(authHeader));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    try {
      setUsage(await aiProvidersApi.usage(undefined, authHeader));
    } catch (err) {
      setUsageError(err instanceof Error ? err.message : String(err));
    }
    try {
      setRouting(await aiProvidersApi.workloads(authHeader));
    } catch (err) {
      setRoutingError(err instanceof Error ? err.message : String(err));
    }
    try {
      setBudgets(await aiProvidersApi.budgets(authHeader));
      setBudgetsError(null);
    } catch (err) {
      setBudgetsError(err instanceof Error ? err.message : String(err));
    }
  }, [authHeader]);

  /** Save budgets; the monthly one moves the Spend card's bar, so reload it. */
  const saveBudgets = useCallback(
    async (body: SetBudgetsRequest) => {
      setBudgetsBusy(true);
      setBudgetsError(null);
      try {
        setBudgets(await aiProvidersApi.setBudgets(body, authHeader));
        setUsage(await aiProvidersApi.usage(undefined, authHeader));
      } catch (err) {
        setBudgetsError(err instanceof Error ? err.message : String(err));
      } finally {
        setBudgetsBusy(false);
      }
    },
    [authHeader],
  );

  /**
   * Apply one assignment. The PUT returns the whole refreshed table, so
   * every row that INHERITS from the one just changed updates too — a
   * local patch would leave those rows showing a stale effective provider,
   * which is the exact confusion this surface exists to remove.
   */
  const applyRouting = useCallback(
    async (body: SetWorkloadProviderRequest) => {
      setRoutingBusy(`${body.scope}:${body.key}`);
      setRoutingError(null);
      try {
        setRouting(await aiProvidersApi.setWorkload(body, authHeader));
      } catch (err) {
        setRoutingError(err instanceof Error ? err.message : String(err));
      } finally {
        setRoutingBusy(null);
      }
    },
    [authHeader],
  );

  /** "Make all N follow this group": clear every feature override in the
   * group, one PUT each (the gateway sets one assignment per call); the last
   * response is the whole refreshed table. */
  const followGroup = useCallback(
    async (group: WorkloadGroupRow) => {
      setRoutingBusy(`group:${group.key}`);
      setRoutingError(null);
      try {
        let table: WorkloadRoutingStatus | null = null;
        for (const w of group.workloads) {
          if (!w.override_provider && !w.override_model) continue;
          table = await aiProvidersApi.setWorkload(
            { scope: "workload", key: w.key, provider: null, model: null },
            authHeader,
          );
        }
        if (table) setRouting(table);
      } catch (err) {
        setRoutingError(err instanceof Error ? err.message : String(err));
        setRouting(await aiProvidersApi.workloads(authHeader).catch(() => null));
      } finally {
        setRoutingBusy(null);
        setPendingFollow(null);
      }
    },
    [authHeader],
  );

  /** Pick a provider's default model. The routing table shows effective
   * models too, so it is reloaded alongside. */
  const applyModel = useCallback(
    async (provider: ProviderInfo, model: string | null) => {
      setModelBusy(provider.id);
      setError(null);
      try {
        setStatus(await aiProvidersApi.setModel(provider.id, model, authHeader));
        setRouting(await aiProvidersApi.workloads(authHeader));
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setModelBusy(null);
      }
    },
    [authHeader],
  );

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h2"
          id="ai-providers-heading"
          sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}
        >
          AI Providers
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
          The LLM provider the AI gateway uses for non-streaming agent
          completions. Switching takes effect at runtime and is kept across
          gateway restarts. Streaming chat (Otto &amp; support) always uses
          Anthropic, and any per-call provider override still wins.
        </Typography>
      </Box>

      {error && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            bgcolor: (t) => alpha(t.palette.error.main, 0.1),
            px: 2,
            py: 1.5,
            fontSize: 14,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>
            Couldn’t load AI providers
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>{error}</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
            If this says the gateway is unreachable, confirm{" "}
            <Box component="code">AI_GATEWAY_URL</Box> is set for this dashboard and the
            gateway is up.
          </Typography>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => void refresh()}
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Box>
      )}

      {status === null && !error && (
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>Loading…</Typography>
      )}

      {status && (
        <>
          <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
            Active provider:{" "}
            <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
              {labelFor(status, status.active)}
            </Box>
            {status.active !== status.env_default && (
              <Box component="span" sx={{ ml: 1, fontSize: 12 }}>
                (boot default: {labelFor(status, status.env_default)})
              </Box>
            )}
          </Typography>

          <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
            {status.providers.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                onSetActive={() => setPendingSwitch(p)}
                onSetModel={(model) => void applyModel(p, model)}
                modelBusy={modelBusy === p.id}
              />
            ))}
          </Box>

          <ProviderRolesCard roles={status.roles} />
        </>
      )}

      {routing ? (
        <WorkloadRoutingCard
          routing={routing}
          onChange={(body) => void applyRouting(body)}
          onFollowGroup={(group) => setPendingFollow(group)}
          busyKey={routingBusy}
          error={routingError}
        />
      ) : null}

      <BudgetsCard
        budgets={budgets}
        error={budgetsError}
        busy={budgetsBusy}
        onSave={(body) => void saveBudgets(body)}
      />

      <SpendSection usage={usage} error={usageError} />

      <ConfirmDialog
        isOpen={pendingFollow !== null}
        onClose={() => setPendingFollow(null)}
        title={`Make every ${pendingFollow?.label ?? ""} feature follow the group?`}
        description="Clears the provider and model set on each feature inside this group, so all of them run on what the group row says. You can still set a feature separately afterwards."
        confirmLabel="Make all follow the group"
        variant="default"
        onConfirm={async () => {
          if (pendingFollow) await followGroup(pendingFollow);
        }}
      />

      <ConfirmDialog
        isOpen={pendingSwitch !== null}
        onClose={() => setPendingSwitch(null)}
        title={`Switch active AI provider to ${pendingSwitch?.label ?? ""}?`}
        description="Routes the gateway's non-streaming agent completions (sales agent + server-to-server) to this provider. Streaming chat stays on Anthropic, and per-call provider overrides are unaffected."
        confirmLabel="Switch provider"
        variant="default"
        onConfirm={async () => {
          if (!pendingSwitch) return;
          try {
            setStatus(await aiProvidersApi.setActive(pendingSwitch.id, authHeader));
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
            throw err; // keep the dialog open so the user can retry
          }
          setPendingSwitch(null);
        }}
      />
    </Box>
  );
}
